import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../axios';
import { extractErrorMessage } from '../errors';
import toast from 'react-hot-toast';
import type { ApiResponse } from '../types/common.types';
import type { PackagePickupRequest, CreatePickupRequest, KioskPickupScanResult } from '../types/packagePickup.types';

export const PICKUP_QUEUE_KEY = 'packagePickup';

export function usePickupQueue() {
  return useQuery({
    queryKey: [PICKUP_QUEUE_KEY, 'queue'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<PackagePickupRequest[]>>(
        '/packagepickup/queue'
      );
      return data;
    },
    // Live queue - the timer on each card counts wait-time itself, but a periodic refetch keeps
    // things correct even if a SignalR event is ever missed (dropped connection, etc).
    refetchInterval: 30_000,
  });
}

export function usePickupArchive() {
  return useQuery({
    queryKey: [PICKUP_QUEUE_KEY, 'archive'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<PackagePickupRequest[]>>(
        '/packagepickup/archive'
      );
      return data;
    },
    // Historical/delivered list - not live like the active queue, no need to poll.
  });
}

export function useCreatePickupRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreatePickupRequest) => {
      const { data } = await axiosInstance.post<ApiResponse<PackagePickupRequest>>(
        '/packagepickup',
        request
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת בקשת איסוף'));
    },
  });
}

// Called by the kiosk with the raw OCR'd Cheetah message text - classification and queue-entry
// creation both happen server-side (see PackagePickupController.KioskScan's comment on why),
// this just reports what happened so the kiosk screen knows which customer-facing message to show.
export function useKioskPickupScan() {
  return useMutation({
    mutationFn: async (text: string) => {
      const { data } = await axiosInstance.post<ApiResponse<KioskPickupScanResult>>(
        '/packagepickup/kiosk-scan',
        { text }
      );

      if (!data.success || !data.data) {
        throw new Error(data.message || 'הסריקה נכשלה');
      }

      return data.data;
    },
  });
}

export function useStartHandlingPickup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosInstance.post<ApiResponse<void>>(`/packagepickup/${id}/start`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון הבקשה'));
    },
  });
}

export function useMarkPickupFound() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosInstance.post<ApiResponse<void>>(`/packagepickup/${id}/found`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון הבקשה'));
    },
  });
}

export function useDeliverPickup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosInstance.post<ApiResponse<void>>(`/packagepickup/${id}/deliver`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
      toast.success('החבילה נמסרה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון הבקשה'));
    },
  });
}

export function useCancelPickupRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosInstance.delete<ApiResponse<void>>(`/packagepickup/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
      toast.success('הבקשה בוטלה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בביטול הבקשה'));
    },
  });
}
